import { useState } from "react";
import { MessageCircle, CalendarCheck, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Field, Empty } from "./common";
import { api, errMsg } from "../lib/api";
import { addDays, todayISO, fmtDate } from "../lib/format";

function useWhatsApp(siteId, defaultDay) {
  const [day, setDay] = useState(defaultDay || addDays(todayISO(), 1));
  const [msgs, setMsgs] = useState(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const call = async (path, d, onOk) => {
    setBusy(true);
    try {
      const r = await api.post(path, { day: d, site_id: siteId || null });
      setMsgs(r.data.messages);
      onOk?.(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const preview = (d = day) => { setSent(false); return call("/integrations/whatsapp/preview", d); };
  const send = () => call("/integrations/whatsapp/send-shifts", day, (data) => {
    setSent(true);
    toast.success(`✓ Messaggio inviato a ${data.sent} operai (simulazione)`);
  });
  const changeDay = (d) => { setDay(d); preview(d); };
  return { day, msgs, sent, busy, preview, send, changeDay };
}

function WhatsAppMessages({ busy, msgs, sent }) {
  if (busy && !msgs) return <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (!msgs?.length) return <Empty title="Nessun operaio assegnato in questa data" />;
  return msgs.map((m) => (
    <div key={`${m.worker_id}-${m.site_name}`} className="rounded-lg border border-border bg-[#0b1a14] p-3" data-testid={`whatsapp-msg-${m.worker_id}`}>
      <div className="flex justify-between text-xs text-muted-foreground mb-1.5"><span className="font-semibold text-foreground">{m.recipient}</span><span className="num">{m.phone}</span></div>
      <div className="text-sm rounded-lg bg-emerald-900/40 border border-emerald-800 p-2.5">{m.message}</div>
      {sent && <div className="text-xs text-emerald-400 mt-1.5 flex items-center gap-1" data-testid={`whatsapp-sent-${m.worker_id}`}><Check className="h-3.5 w-3.5" />✓ Messaggio inviato</div>}
    </div>
  ));
}

export function WhatsAppButton({ siteId, defaultDay, className }) {
  const [open, setOpen] = useState(false);
  const { day, msgs, sent, busy, preview, send, changeDay } = useWhatsApp(siteId, defaultDay);
  const count = msgs?.length || 0;
  return (
    <>
      <Button className={className || "bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"} onClick={() => { setOpen(true); preview(); }} data-testid="whatsapp-send-shifts-btn">
        <MessageCircle className="h-4 w-4 mr-2" /> INVIA TURNI VIA WHATSAPP
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-2xl" data-testid="whatsapp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageCircle className="h-5 w-5 text-emerald-400" />Invio turni via WhatsApp</DialogTitle>
            <DialogDescription>Simulazione — predisposto per WhatsApp Business API (Meta). Nessun messaggio reale viene inviato.</DialogDescription>
          </DialogHeader>
          <div className="flex items-end gap-2">
            <Field label="Turni del giorno" className="flex-1">
              <Input type="date" value={day} onChange={(e) => changeDay(e.target.value)} data-testid="whatsapp-date-input" />
            </Field>
          </div>
          <div className="max-h-[45vh] overflow-y-auto scrollbar-thin space-y-2">
            <WhatsAppMessages busy={busy} msgs={msgs} sent={sent} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Chiudi</Button>
            <Button onClick={send} disabled={busy || !count || sent} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="whatsapp-confirm-send-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MessageCircle className="h-4 w-4 mr-2" />}{sent ? "Inviati" : `Invia ${count} messaggi`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function GoogleCalendarButton({ start, end, siteId }) {
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const sync = async () => {
    setOpen(true);
    setBusy(true);
    setRes(null);
    try {
      const r = await api.post("/integrations/google-calendar/sync", { start_date: start, end_date: end, site_id: siteId || null });
      setRes(r.data);
      toast.success(`✓ ${r.data.synced} eventi sincronizzati (simulazione)`);
    } catch (e) {
      toast.error(errMsg(e));
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button variant="outline" onClick={sync} className="border-sky-600 text-sky-200 hover:bg-sky-950" data-testid="gcal-sync-btn">
        <CalendarCheck className="h-4 w-4 mr-2" /> SINCRONIZZA GOOGLE CALENDAR
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-xl" data-testid="gcal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarCheck className="h-5 w-5 text-sky-400" />Google Calendar</DialogTitle>
            <DialogDescription>Simulazione — architettura predisposta per Google Calendar API (OAuth2). Periodo {fmtDate(start)} → {fmtDate(end)}.</DialogDescription>
          </DialogHeader>
          {busy ? <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div> : res && (
            <div className="max-h-[50vh] overflow-y-auto scrollbar-thin space-y-2">
              {!res.events.length && <Empty title="Nessun evento nel periodo" />}
              {res.events.map((e) => (
                <div key={e.provider_id} className="rounded-lg border border-border p-3 flex items-center gap-3" data-testid={`gcal-event-${e.provider_id}`}>
                  <Check className="h-5 w-5 text-emerald-400 shrink-0" />
                  <div className="min-w-0"><div className="text-sm font-medium truncate">{e.summary}</div><div className="text-xs text-muted-foreground">{fmtDate(e.start)} → {fmtDate(e.end)} · ✓ Evento sincronizzato</div></div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
